import * as styles from "./ContactPreview.css";

import { Factory, UserRound } from "lucide-react";
import { Fragment } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import type { IconName } from "@frontend/components/atoms/Icon";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";

import {
  ContactAddressPlaceLabels,
  ContactEmailPlaceLabels,
  ContactPhonePlaceLabels,
  ContactUrlPlaceLabels,
  type PlaceLabel,
} from "../../i18n";
import type {
  ContactPreviewAddress,
  ContactPreviewData,
} from "../../schemas/contactPreview";

/**
 * A contact's card, read-only. The address book's own detail panel is bound to
 * the form that edits it; this one only ever displays, so it takes the contact
 * as a prop and can be shown anywhere, several times over.
 */
export function ContactPreview({
  contact,
}: {
  readonly contact: ContactPreviewData;
}) {
  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <Avatar
          url={contact.hasPhoto ? contact.avatarUrl : undefined}
          fallback={contact.isOrganization ? <Factory /> : <UserRound />}
          size="md"
        />

        <div className={styles.names}>
          <span className={styles.displayName}>{contact.displayName}</span>

          {/* An organization contact already carries its name as the display
              name, repeating it below would say nothing */}
          {contact.organization !== "" && !contact.isOrganization && (
            <span className={styles.organization}>{contact.organization}</span>
          )}
        </div>
      </header>

      {contact.phones.length > 0 && (
        <Section icon="Phone" title={<Trans>Phones</Trans>}>
          {contact.phones.map((phone, index) => (
            <Row
              key={index}
              labels={ContactPhonePlaceLabels}
              place={phone.place}
            >
              {phone.number}
            </Row>
          ))}
        </Section>
      )}

      {contact.emails.length > 0 && (
        <Section icon="Mail" title={<Trans>Emails</Trans>}>
          {contact.emails.map((email, index) => (
            <Row
              key={index}
              labels={ContactEmailPlaceLabels}
              place={email.place}
            >
              <a className={styles.link} href={`mailto:${email.address}`}>
                {email.address}
              </a>
            </Row>
          ))}
        </Section>
      )}

      {contact.urls.length > 0 && (
        <Section icon="Link" title={<Trans>URLs</Trans>}>
          {contact.urls.map((url, index) => (
            <Row key={index} labels={ContactUrlPlaceLabels} place={url.place}>
              <a
                className={styles.link}
                href={url.url}
                target="_blank"
                rel="noreferrer"
              >
                {url.url}
              </a>
            </Row>
          ))}
        </Section>
      )}

      {contact.typedAddresses.length > 0 && (
        <Section icon="MapPinHouse" title={<Trans>Addresses</Trans>}>
          {contact.typedAddresses.map((address, index) => (
            <Row
              key={index}
              labels={ContactAddressPlaceLabels}
              place={address.place}
            >
              <AddressLines address={address} />
            </Row>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  readonly icon: IconName;
  readonly title: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  return (
    <Fieldset variant="plain">
      <FieldsetLegend icon={icon}>{title}</FieldsetLegend>

      <div className={styles.rows}>{children}</div>
    </Fieldset>
  );
}

function Row({
  labels,
  place,
  children,
}: {
  readonly labels: PlaceLabel;
  readonly place: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Fragment>
      <span className={styles.place}>
        <PlaceText labels={labels} place={place} />
      </span>

      <div className={styles.value}>{children}</div>
    </Fragment>
  );
}

/**
 * Places are stored as vCard keywords. Anything the address book does not know
 * a label for is shown as it was stored rather than dropped.
 */
function PlaceText({
  labels,
  place,
}: {
  readonly labels: PlaceLabel;
  readonly place: string;
}) {
  const { _ } = useLingui();

  const key = place.toUpperCase();

  return <>{key in labels ? _(labels[key]) : place}</>;
}

function AddressLines({
  address,
}: {
  readonly address: ContactPreviewAddress;
}) {
  const lines = [
    address.street,
    [address.extendedAddress, address.postOfficeBox].filter(Boolean).join(" "),
    [address.postalCode, address.city].filter(Boolean).join(" "),
    address.state,
    address.country,
  ].filter((line) => line !== "");

  return (
    <div className={styles.addressLines}>
      {lines.map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </div>
  );
}
